import { useEffect, useMemo, useState } from 'react'
import { CheckCircle2, RotateCcw, Wallet } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { cn } from '@/utils/cn'
import { formatCurrency } from '@/utils/format-currency'
import type { PartnerCommissionRow, PartnerCommissionStatus } from '@/types'
import { PartnerBatchPaymentModal } from './PartnerBatchPaymentModal'
import type { BatchPaymentForm } from './usePartnerPayments'

const STATUS_STYLE: Record<PartnerCommissionStatus, { label: string; className: string }> = {
  pendente: { label: 'Pendente', className: 'bg-[var(--glass-bg)] text-[var(--text-muted)]' },
  aprovada: { label: 'A pagar', className: 'bg-amber-500/10 text-amber-500' },
  paga: { label: 'Paga', className: 'bg-emerald-500/10 text-emerald-500' },
}

export function formatCompetencia(row: Pick<PartnerCommissionRow, 'competencia' | 'dataReferencia' | 'parcelaAtual' | 'totalParcelas'>) {
  const { competencia, dataReferencia } = row
  if (competencia === 'UNICA') return 'Pagamento único'
  if (competencia && /^P\d+$/.test(competencia)) {
    const parcela = row.parcelaAtual ?? Number(competencia.slice(1))
    const total = row.totalParcelas ? `/${row.totalParcelas}` : ''
    return `Parcela ${parcela}${total} · ${new Date(dataReferencia).toLocaleDateString('pt-BR')}`
  }
  if (competencia && /^\d{4}-\d{2}$/.test(competencia)) {
    const [year, month] = competencia.split('-')
    return `${month}/${year}`
  }
  return new Date(dataReferencia).toLocaleDateString('pt-BR')
}

interface PartnerCommissionsTableProps {
  rows: PartnerCommissionRow[]
  loading: boolean
  busy: boolean
  canApprove: boolean
  canPay: boolean
  meta: { totalItems: number; totalPages: number; totalValor: number }
  page: number
  onPageChange: (page: number) => void
  onUpdateStatus: (ids: string[], status: 'pendente' | 'aprovada') => void
  onRegisterPayment: (ids: string[], form: BatchPaymentForm) => Promise<boolean>
}

export function PartnerCommissionsTable({
  rows,
  loading,
  busy,
  canApprove,
  canPay,
  meta,
  page,
  onPageChange,
  onUpdateStatus,
  onRegisterPayment,
}: PartnerCommissionsTableProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [paymentOpen, setPaymentOpen] = useState(false)

  useEffect(() => {
    setSelected(new Set())
  }, [rows])

  const openRows = rows.filter((r) => r.status !== 'paga')
  const selectedRows = useMemo(() => rows.filter((r) => selected.has(r.id)), [rows, selected])
  const selectedTotal = selectedRows.reduce((sum, r) => sum + r.valor, 0)
  const selectedPartners = new Set(selectedRows.map((r) => r.parceiro?.id)).size
  const allOpenSelected = openRows.length > 0 && openRows.every((r) => selected.has(r.id))
  const selectable = canApprove || canPay

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    setSelected(allOpenSelected ? new Set() : new Set(openRows.map((r) => r.id)))
  }

  const ids = selectedRows.map((r) => r.id)
  const pendingIds = selectedRows.filter((r) => r.status === 'pendente').map((r) => r.id)
  const approvedIds = selectedRows.filter((r) => r.status === 'aprovada').map((r) => r.id)

  return (
    <div className='glass-card p-0 overflow-hidden'>
      <div className='flex flex-wrap items-center justify-between gap-2 border-b border-[var(--glass-border)] px-4 py-3'>
        <p className='text-xs text-[var(--font-color-muted)]'>
          {meta.totalItems} comissões · total filtrado{' '}
          <strong className='text-[var(--font-color-body)]'>{formatCurrency(meta.totalValor)}</strong>
        </p>
        {selectable && selectedRows.length > 0 && (
          <div className='flex flex-wrap items-center gap-2'>
            <span className='text-xs text-[var(--font-color-muted)]'>
              {selectedRows.length} selecionada(s) · {formatCurrency(selectedTotal)}
            </span>
            {canApprove && pendingIds.length > 0 && (
              <Button variant='secondary' size='sm' icon={<CheckCircle2 size={13} />} disabled={busy} onClick={() => onUpdateStatus(pendingIds, 'aprovada')}>
                Aprovar
              </Button>
            )}
            {canApprove && approvedIds.length > 0 && (
              <Button variant='ghost' size='sm' icon={<RotateCcw size={13} />} disabled={busy} onClick={() => onUpdateStatus(approvedIds, 'pendente')}>
                Voltar p/ pendente
              </Button>
            )}
            {canPay && (
              <Button variant='primary' size='sm' icon={<Wallet size={13} />} disabled={busy} onClick={() => setPaymentOpen(true)}>
                Registrar pagamento
              </Button>
            )}
          </div>
        )}
      </div>

      <div className='overflow-x-auto'>
        <table className='w-full'>
          <thead>
            <tr className='border-b border-[var(--glass-border)]'>
              {selectable && (
                <th className='w-10 px-4 py-3'>
                  <input type='checkbox' checked={allOpenSelected} onChange={toggleAll} aria-label='Selecionar todas em aberto' />
                </th>
              )}
              {['Competência', 'Parceiro', 'Deal / Cliente', 'Nível', 'Valor', 'Status'].map((h) => (
                <th key={h} className='text-left px-4 py-3 text-[var(--font-color-label)] font-medium uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const status = STATUS_STYLE[row.status] ?? STATUS_STYLE.pendente
              const empresa = row.deal?.empresa?.nomeFantasia || row.deal?.empresa?.nome
              return (
                <tr key={row.id} className='border-b border-[var(--glass-border)] last:border-0 hover:bg-[var(--glass-bg)] transition-colors'>
                  {selectable && (
                    <td className='px-4 py-3'>
                      <input
                        type='checkbox'
                        disabled={row.status === 'paga'}
                        checked={selected.has(row.id)}
                        onChange={() => toggle(row.id)}
                        aria-label={`Selecionar comissão de ${row.parceiro?.nome ?? ''}`}
                      />
                    </td>
                  )}
                  <td className='px-4 py-3 text-sm text-[var(--font-color-body)]'>{formatCompetencia(row)}</td>
                  <td className='px-4 py-3 text-sm font-medium text-[var(--font-color-body)]'>{row.parceiro?.nome ?? '—'}</td>
                  <td className='px-4 py-3'>
                    <p className='text-sm text-[var(--font-color-body)]'>{empresa ?? row.deal?.nome ?? '—'}</p>
                    {empresa && row.deal?.nome && <p className='text-[11px] text-[var(--font-color-muted)]'>{row.deal.nome}</p>}
                  </td>
                  <td className='px-4 py-3'>
                    {row.nivel != null ? (
                      <span className='text-xs text-[var(--font-color-body)]'>
                        N{row.nivel}
                        {row.nivel > 1 && row.parceiroOrigem && (
                          <span className='block text-[10px] text-[var(--font-color-muted)]'>via {row.parceiroOrigem.nome}</span>
                        )}
                      </span>
                    ) : (
                      <span className='text-xs text-[var(--font-color-muted)]'>Direta</span>
                    )}
                  </td>
                  <td className='px-4 py-3 text-sm font-bold text-[var(--font-color-body)]'>{formatCurrency(row.valor)}</td>
                  <td className='px-4 py-3'>
                    <span className={cn('px-2 py-0.5 rounded-md text-[10px] font-bold uppercase', status.className)}>{status.label}</span>
                    {row.status === 'paga' && row.dataPagamento && (
                      <span className='block mt-0.5 text-[10px] text-[var(--font-color-muted)]'>
                        em {new Date(row.dataPagamento).toLocaleDateString('pt-BR')}
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={selectable ? 7 : 6} className='px-4 py-10 text-center text-sm text-[var(--font-color-muted)]'>
                  Nenhuma comissão encontrada para os filtros
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {meta.totalPages > 1 && (
        <div className='flex items-center justify-end gap-2 border-t border-[var(--glass-border)] px-4 py-3 text-xs text-[var(--font-color-muted)]'>
          <Button variant='ghost' size='sm' disabled={page <= 1} onClick={() => onPageChange(page - 1)}>Anterior</Button>
          <span>Página {page} de {meta.totalPages}</span>
          <Button variant='ghost' size='sm' disabled={page >= meta.totalPages} onClick={() => onPageChange(page + 1)}>Próxima</Button>
        </div>
      )}

      <PartnerBatchPaymentModal
        open={paymentOpen}
        busy={busy}
        total={selectedTotal}
        commissionsCount={selectedRows.length}
        partnersCount={selectedPartners}
        onClose={() => setPaymentOpen(false)}
        onConfirm={async (form) => {
          const ok = await onRegisterPayment(ids, form)
          if (ok) setPaymentOpen(false)
        }}
      />
    </div>
  )
}
